import { ChangeDetectionStrategy, Component, EventEmitter, inject, input, InputSignal, Output } from '@angular/core';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faRefresh } from '@fortawesome/pro-light-svg-icons';
import { IconModule } from "@ogs-gmbh/ngx-m3-components";
import { OVERVIEW_HEADER_OPERATOR_STRATEGY, OverviewHeaderOperatorStrategy } from '../../types/overview-header-operator-strategy.type';
import { TableDataService } from '@core/services/table/table-data.service';
import { MatIconButton } from '@angular/material/button';
// eslint-disable-next-line @tseslint/no-unused-vars
import { OverviewHeaderComponent } from '@shared/components/overview-header/overview-header.component';
// eslint-disable-next-line @tseslint/no-unused-vars
import { OverviewHeaderOperatorsComponent } from '../../overview-header-operators.component';

/**
 * Add this Component to the {@link OverviewHeaderOperatorsComponent | app-overview-header-operators}
 * to provide a refresh operator inside the {@link OverviewHeaderComponent | app-overview-header}.
 * This Component allows the user to trigger a data refresh, either by emitting an event or by
 * reloading a provided {@link TableDataService}.
 *
 * This operator supports the following behaviors based on the applied strategy:
 * - `emit`: Emits a `refresh` event that can be handled by the parent component
 * - `handle`: Automatically reloads the injected `TableDataService`
 *
 * @example
 *
 * ```html
 * <app-overview-header>
 *   <app-overview-header-operators>
 *     <app-overview-header-refresh />
 *   </app-overview-header-operators>
 * </app-overview-header>
 * ```
 */

@Component({
  selector: 'app-overview-header-refresh',
  standalone: true,
  imports: [ IconModule, MatIconButton ],
  templateUrl: './overview-header-refresh.component.html',
  styleUrl: './overview-header-refresh.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OverviewHeaderRefreshComponent {
  private readonly _faIconLibrary: FaIconLibrary = inject(FaIconLibrary);

  private readonly _tableDataService: TableDataService<unknown> | null = inject(TableDataService, { optional: true });

  @Output() public readonly refresh: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();

  public strategy: InputSignal<OverviewHeaderOperatorStrategy> = input<OverviewHeaderOperatorStrategy>(OVERVIEW_HEADER_OPERATOR_STRATEGY.handle);

  constructor () {
    this._faIconLibrary.addIcons(faRefresh);
  }

  protected _refresh (mouseEvent: MouseEvent): void {
    if (this.strategy() === OVERVIEW_HEADER_OPERATOR_STRATEGY.emit)
      this.refresh.emit(mouseEvent);

    else if (this._tableDataService)
      this._tableDataService.reload();
  }
}
